import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { api, apiErrorMessage } from '../lib/api-client';
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_HELP,
  failedAttachmentUpload,
  firstAttachmentRejection,
} from '../lib/timeOffAttachments';
import { Alert, Button, DrawerShell, FormField, Input, Select, Textarea, useToast } from '@payforge/ui';
import { DiscardChangesDialog } from '../components/DiscardChangesDialog';
import { FormDatePicker } from '../components/FormDatePicker';
import { BalanceTileGrid } from '../components/BalanceTileGrid';
import { balanceToTile } from '../lib/balanceTiles';
import { formatDayMonthYear, formatIsoDate } from '../lib/dates';
import {
  LEAVE_FORM_NOTE_LABEL,
  awaitsOwnSignature,
  useLeaveFormRequired,
  useOpenLeaveForm,
} from '../lib/useLeaveDocuments';
import { useReminderStatus } from '../lib/useReminderStatus';
import { periodLabel } from './timesheet/period';
import type {
  EnrolledBalanceDto,
  HolidayDto,
  SubmitTimeOffRequestCommand,
  TimeOffCategoryDto,
  TimeOffRequestDto,
} from '../lib/types';
import { AmountPerDayTable } from './my-info/time-off/AmountPerDayTable';
import {
  buildDayRows,
  enumerateDays,
  uniqueMonths,
  type DayRow,
} from './my-info/time-off/amountPerDayUtils';
import { usePermissions } from '../lib/usePermissions';

type MonthView = { year: number; month: number; holidays: HolidayDto[] };

export function RequestTimeOffDrawer({
  employeeId,
  categories,
  balances,
  onClose,
  onSubmitted,
  targetEmployeeId,
  subjectLocationId,
}: {
  employeeId: number;
  categories: TimeOffCategoryDto[];
  /** Per-category balances for the requesting employee; drives the top tiles. */
  balances?: EnrolledBalanceDto[];
  onClose: () => void;
  /** The request is recorded and needs nothing more from the submitter on this page. */
  onSubmitted: (created: TimeOffRequestDto) => void;
  /** When set, the request is submitted on behalf of this employee (admin only). */
  targetEmployeeId?: number;
  /** The location of the employee whose leave this is, whose leave may need a signed leave request form. */
  subjectLocationId?: number | null;
}) {
  const leaveFormRequired = useLeaveFormRequired(subjectLocationId);
  const openLeaveForm = useOpenLeaveForm();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [note, setNote] = useState('');
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [rowAmounts, setRowAmounts] = useState<Record<string, number>>({});
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [showDiscard, setShowDiscard] = useState(false);

  const isOnBehalf = targetEmployeeId !== undefined;
  const submitLabel = leaveFormRequired && !isOnBehalf ? 'Sign and send' : 'Send request';
  const noteLabel = leaveFormRequired ? LEAVE_FORM_NOTE_LABEL : 'Note';
  // Whose leave this is. Holidays are location-scoped server-side, so an on-behalf request must ask
  // for the SUBJECT's holidays, not the viewer's, or the per-day table clamps the wrong days to 0.
  const subjectEmployeeId = targetEmployeeId ?? employeeId;

  // Employees cannot back-date a request (the picker lower bound is today), mirroring the server
  // rule that only an admin may record time off starting in the past (CU 869dxzghj). Admins keep
  // the unbounded picker so they can deliberately submit past dates on anyone's behalf.
  const { isAdmin } = usePermissions();
  const minDate = isAdmin ? undefined : formatIsoDate(new Date());

  // Closing with unsaved input first asks to confirm the discard (mirrors the My Info Record Time
  // Off drawer), so the Home and My Info entry points behave the same.
  const isDirty =
    start !== '' ||
    end !== '' ||
    note !== '' ||
    categoryId !== '' ||
    Object.keys(rowAmounts).length > 0 ||
    attachmentFiles.length > 0;

  // Tiles reflect the employee's actually enrolled policies (one per enrollment).
  const tiles = (balances ?? []).map((b) => balanceToTile(b, 'days available'));
  const hasCategories = categories.length > 0;

  // Fetch holidays for all months in the selected range.
  const monthsToFetch = start && end ? uniqueMonths(start, end) : [];

  const holidayResults = useQueries({
    queries: monthsToFetch.map((m) => ({
      queryKey: ['calendar', 'month', m.year, m.month, subjectEmployeeId],
      queryFn: () =>
        api.get<MonthView>(
          `/v1/calendar/month?year=${m.year}&month=${m.month}&employeeId=${subjectEmployeeId}`,
        ),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const allHolidays = new Map<string, HolidayDto>();
  for (const q of holidayResults) {
    for (const h of (q.data as MonthView | undefined)?.holidays ?? []) {
      allHolidays.set(h.date, h);
    }
  }

  // The enrolled policy's half-day opt-in for the chosen category (balances carry the flag).
  const allowHalfDays =
    categoryId !== '' ? (balances?.find((b) => b.categoryId === categoryId)?.allowHalfDays ?? false) : false;

  const days = enumerateDays(start, end);
  const dayRows = buildDayRows(days, allHolidays);
  const mergedRows: DayRow[] = dayRows.map((r) => ({
    ...r,
    amount: r.isHoliday ? 0 : (rowAmounts[r.date] ?? r.amount),
  }));

  // Fetch existing approved/pending requests to warn when selected range overlaps.
  const bookedQuery = useQuery({
    queryKey: isOnBehalf
      ? ['employee', targetEmployeeId, 'time-off']
      : ['time-off', 'requests', 'mine'],
    queryFn: isOnBehalf
      ? () => api.get<TimeOffRequestDto[]>(`/v1/employees/${targetEmployeeId}/time-off`)
      : () => api.get<TimeOffRequestDto[]>('/v1/time-off/requests'),
    staleTime: 60 * 1000,
  });

  // Days actually taken by existing active requests: the request's range minus its excludedDates
  // (zeroed-out days). A day excluded by an existing request stays free to request again, matching
  // the server's day-level overlap check (CU 869e2kfum).
  const bookedDates = useMemo(() => {
    const set = new Set<string>();
    for (const req of (bookedQuery.data ?? [])) {
      if (req.status !== 'APPROVED' && req.status !== 'PENDING') continue;
      const excluded = new Set(req.excludedDates ?? []);
      const s = new Date(req.startDate + 'T00:00:00');
      const e = new Date(req.endDate + 'T00:00:00');
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const iso = d.toLocaleDateString('en-CA');
        if (!excluded.has(iso)) set.add(iso);
      }
    }
    return set;
  }, [bookedQuery.data]);

  // Only the days this request actually takes (amount 1) can collide; a day the requester zeroes
  // out is not being booked, so it never conflicts.
  const hasOverlap = useMemo(
    () => mergedRows.some((r) => r.amount > 0 && bookedDates.has(r.date)),
    [mergedRows, bookedDates],
  );

  // Courtesy nudge: leave spanning the running timesheet period's deadline usually means the
  // sheet gets forgotten, so warn while the caller still owes hours for it. The status is the
  // CALLER's own, so an on-behalf record never shows it (the subject's timesheet is unknown
  // here). Comparing ISO date strings is safe (they sort lexicographically).
  const reminderStatus = useReminderStatus();
  const [timesheetNoticeDismissed, setTimesheetNoticeDismissed] = useState(false);
  const currentPeriod = reminderStatus.data?.currentPeriod ?? null;
  const showTimesheetNotice =
    !isOnBehalf &&
    currentPeriod !== null &&
    currentPeriod.owes &&
    start !== '' &&
    end !== '' &&
    start <= currentPeriod.deadlineDate &&
    currentPeriod.deadlineDate <= end;

  // Dismissal lives only for the matching selection: once the range stops covering the deadline
  // it resets (state adjusted during render, matching the TimesheetPage derived-state idiom), so
  // a future matching booking warns again. Reopening the drawer resets it too (fresh mount).
  if (!showTimesheetNotice && timesheetNoticeDismissed) setTimesheetNoticeDismissed(false);

  // Editing the request clears any stale server rejection (e.g. the balance-overdraft message),
  // so correcting the input leaves only the recalculated result on screen - matching the My Info
  // Record Time Off drawer (CU 869e1qfjz).
  function handleStartChange(v: string) {
    setError(null);
    setStart(v);
    setRowAmounts({});
    if (end && v > end) setEnd('');
  }

  function handleEndChange(v: string) {
    setError(null);
    setEnd(v);
    setRowAmounts({});
  }

  function handleAmountChange(date: string, val: number) {
    setError(null);
    setRowAmounts((prev) => ({ ...prev, [date]: val }));
  }

  function handleAttachmentChange(e: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    const rejection = firstAttachmentRejection(picked);
    if (rejection) {
      setError(rejection);
      e.target.value = '';
      setAttachmentFiles([]);
      return;
    }
    setError(null);
    setAttachmentFiles(picked);
  }

  function handleClose() {
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    onClose();
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Always send the real taken set: an all-zeroed table must reach the server as an empty
    // list (rejected with a clear message), not as null = "default whole-range model".
    const taken = mergedRows.filter((r) => r.amount > 0);
    const takenDates = taken.map((r) => r.date);
    // The wire always describes exactly what the table shows: every fractional taken row is sent as
    // a DayAmount regardless of the current allowHalfDays, so the server is the single authority on
    // whether the fraction is allowed (it fails loudly instead of silently booking a whole day).
    const fractional = taken.filter((r) => r.amount < 1).map((r) => ({ date: r.date, amount: r.amount }));
    const dayAmounts = fractional.length > 0 ? fractional : undefined;
    const cmd: SubmitTimeOffRequestCommand = {
      employeeId: isOnBehalf ? targetEmployeeId : employeeId,
      categoryId: Number(categoryId),
      startDate: start,
      endDate: end,
      note: optionalString(note),
      takenDates,
      dayAmounts,
      ...(isOnBehalf ? { targetEmployeeId } : {}),
    };
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.post<TimeOffRequestDto>('/v1/time-off/requests', cmd);
      const uploadFailure = await failedAttachmentUpload(created.id, attachmentFiles);
      await finishSubmission(created);
      if (uploadFailure) showToast(uploadFailure, 'error');
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Failed to submit request.'));
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Hands a recorded request on: the submitter signs their own leave form next and returns to this
   * page (D21), otherwise the drawer closes. Once the request exists the drawer never stays open on a
   * failure, so a retry can never record it twice.
   */
  async function finishSubmission(created: TimeOffRequestDto) {
    if (awaitsOwnSignature(created, isOnBehalf) && (await openLeaveForm(created.id))) return;
    onSubmitted(created);
  }

  const footer = (
    <div className="flex items-center gap-3">
      <Button size="md"
        type="submit"
        form="request-time-off-form"
        disabled={submitting || hasOverlap}
        title={hasOverlap ? 'Remove the overlapping dates before submitting' : undefined}
      >
        {submitting ? 'Sending…' : submitLabel}
      </Button>
      <Button size="md" type="button" variant="tertiary" onClick={handleClose}>
        Cancel
      </Button>
    </div>
  );

  return (
    <DrawerShell
      title={
        <h2 className="text-xl font-bold text-brand-600">
          {isOnBehalf ? 'Record Time Off' : 'Request Time Off'}
        </h2>
      }
      onClose={handleClose}
      footer={footer}
      bodyClassName="space-y-4 bg-slate-50 px-4 py-5 md:px-6"
    >
      <section className="rounded-xl border border-slate-200 bg-surface p-4">
        {tiles.length > 0 ? (
          <BalanceTileGrid tiles={tiles} />
        ) : (
          <p className="text-sm text-slate-500">
            No time-off policies yet — your balances appear here once you're enrolled in a policy.
          </p>
        )}
      </section>

      <section className="rounded-lg border border-slate-100 bg-surface p-4 md:p-5">
        <form id="request-time-off-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="From">
              <FormDatePicker
                ariaLabel="From"
                name="startDate"
                required
                min={minDate}
                value={start}
                onChange={handleStartChange}
              />
            </FormField>
            <FormField label="To">
              <FormDatePicker
                ariaLabel="To"
                name="endDate"
                required
                min={start || minDate}
                value={end}
                onChange={handleEndChange}
              />
            </FormField>
          </div>

          {showTimesheetNotice && !timesheetNoticeDismissed ? (
            <Alert tone="warning">
              <div
                data-testid="timesheet-booking-notice"
                className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1"
              >
                <p>
                  Your {periodLabel(currentPeriod.periodStart)} timesheet is due{' '}
                  {formatDayMonthYear(currentPeriod.deadlineDate)} and is not submitted yet -
                  submit it before your leave.
                </p>
                <button
                  type="button"
                  onClick={() => setTimesheetNoticeDismissed(true)}
                  aria-label="Dismiss reminder"
                  className="text-sm font-medium underline hover:no-underline"
                >
                  Dismiss
                </button>
              </div>
            </Alert>
          ) : null}

          {hasOverlap ? (
            <div
              role="alert"
              aria-live="assertive"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            >
              Some selected dates already have an approved or pending time-off request. Please choose different dates. Dates that are already booked cannot be requested again until the existing request is removed.
            </div>
          ) : null}

          {days.length > 0 ? (
            <fieldset>
              <legend className="block text-sm font-medium text-slate-700">
                Amount <span className="text-red-500">*</span>
              </legend>
              <div className="mt-2">
                <AmountPerDayTable
                  rows={mergedRows}
                  allowHalfDays={allowHalfDays}
                  onAmountChange={handleAmountChange}
                />
              </div>
            </fieldset>
          ) : (
            <FormField label="Amount">
              <Input
                type="text"
                disabled
                value=""
                placeholder="Set the dates above"
                aria-label="Estimated days (calculated from the dates)"
                className="w-full cursor-default"
              />
            </FormField>
          )}

          <FormField label="Time Off Category">
            <Select
              name="categoryId"
              required
              aria-label="Time Off Category"
              value={categoryId}
              onChange={(e) => {
                setError(null);
                setCategoryId(e.target.value === '' ? '' : Number(e.target.value));
                setRowAmounts({});
              }}
              className="w-full"
            >
              <option value="" disabled>
                {hasCategories ? 'Select a category' : 'No time-off policies'}
              </option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label={noteLabel}>
            <Textarea
              name="note"
              aria-label={noteLabel}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1024}
              rows={3}
              placeholder="Optional"
            />
          </FormField>

          <FormField label="Attachment">
            <input
              type="file"
              name="attachment"
              multiple
              accept={ATTACHMENT_ACCEPT}
              aria-label="Attachment (optional)"
              onChange={handleAttachmentChange}
              className="block w-full cursor-pointer rounded-md border border-slate-200 bg-surface text-sm text-slate-700 file:mr-3 file:cursor-pointer file:rounded-l-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            />
            <p className="mt-1 text-xs text-slate-500">{ATTACHMENT_HELP}</p>
          </FormField>

          {error ? <p className="text-sm text-red-700">{error}</p> : null}
        </form>
      </section>

      {showDiscard ? (
        <DiscardChangesDialog onDiscard={onClose} onCancel={() => setShowDiscard(false)} />
      ) : null}
    </DrawerShell>
  );
}

function optionalString(v: string): string | undefined {
  const s = v.trim();
  return s === '' ? undefined : s;
}
